/**
 * The small Markdown the Assistant writes (its prompt allows no more):
 * paragraphs, bulleted and numbered lists, **bold**, *italics*, `code`, and
 * links — the case's own (`evidence:` · `checklist:` · `ask:` · `task:` ·
 * `file:<key>` · `para:<draft|version>:<document id>:<paragraph id>`) and
 * http(s) pages. Parsed into a tree the renderer turns into React elements:
 * nothing is ever injected as HTML, and a link to anything else stays plain
 * text.
 */

export type CaseLink =
  | { scheme: 'evidence' | 'checklist' | 'ask' | 'task'; id: string }
  | { scheme: 'file'; id: string }
  | { scheme: 'para'; document: 'draft' | 'version'; documentId: string; id: string };

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'code'; text: string }
  | { kind: 'link'; label: string; link: CaseLink }
  | { kind: 'web'; label: string; href: string };

export type Block =
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] };

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const RECORD = new RegExp(`^(evidence|checklist|ask|task):(${UUID})$`);
const FILE = /^file:([A-Za-z][0-9]{1,4})$/;
const PARA = new RegExp(`^para:(draft|version):(${UUID}):([0-9A-Fa-f]{8})$`);

/** A link the screens can open, or null for anything else. */
export function parseCaseLink(href: string): CaseLink | null {
  const trimmed = href.trim();
  const record = RECORD.exec(trimmed);
  if (record) {
    return {
      scheme: record[1] as 'evidence' | 'checklist' | 'ask' | 'task',
      id: record[2].toLowerCase(),
    };
  }
  const file = FILE.exec(trimmed);
  if (file) {
    return { scheme: 'file', id: file[1].toUpperCase() };
  }
  const para = PARA.exec(trimmed);
  if (para) {
    return {
      scheme: 'para',
      document: para[1] as 'draft' | 'version',
      documentId: para[2].toLowerCase(),
      id: para[3].toUpperCase(),
    };
  }
  return null;
}

function webHref(href: string): string | null {
  try {
    const url = new URL(href.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** The inline spans of a line: links first (their labels stay plain), then
 * code, bold and italics. Unclosed markers stay as written. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let plain = '';
  const flush = () => {
    if (plain) {
      out.push({ kind: 'text', text: plain });
      plain = '';
    }
  };
  let i = 0;
  while (i < text.length) {
    const rest = text.slice(i);
    const link = /^\[([^\]\n]+)\]\(([^)\s]+)\)/.exec(rest);
    if (link) {
      const caseLink = parseCaseLink(link[2]);
      const web = caseLink ? null : webHref(link[2]);
      flush();
      if (caseLink) {
        out.push({ kind: 'link', label: link[1], link: caseLink });
      } else if (web) {
        out.push({ kind: 'web', label: link[1], href: web });
      } else {
        out.push({ kind: 'text', text: link[1] });
      }
      i += link[0].length;
      continue;
    }
    const code = /^`([^`\n]+)`/.exec(rest);
    if (code) {
      flush();
      out.push({ kind: 'code', text: code[1] });
      i += code[0].length;
      continue;
    }
    const strong = /^\*\*([^*\n](?:[^\n]*?[^*\n])?)\*\*/.exec(rest);
    if (strong) {
      flush();
      out.push({ kind: 'strong', children: parseInline(strong[1]) });
      i += strong[0].length;
      continue;
    }
    const em = /^[*_]([^*_\s](?:[^\n]*?[^*_\s])?)[*_]/.exec(rest);
    if (em && rest[0] === em[0].at(-1) && !/\w/.test(text[i - 1] ?? '')) {
      flush();
      out.push({ kind: 'em', children: parseInline(em[1]) });
      i += em[0].length;
      continue;
    }
    plain += text[i];
    i += 1;
  }
  flush();
  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** The reply's blocks: a blank line ends a paragraph; consecutive bullet or
 * numbered lines are a list (a line indented under an item continues it). */
export function parseCopilotText(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const endParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const endList = () => {
    if (list) {
      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        items: list.items.map((item) => parseInline(item)),
      });
      list = null;
    }
  };
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      endParagraph();
      endList();
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      endParagraph();
      const ordered = numbered !== null;
      if (list && list.ordered !== ordered) {
        endList();
      }
      list ??= { ordered, items: [] };
      list.items.push(item[1]);
      continue;
    }
    if (list && /^\s+/.test(raw) && list.items.length) {
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    endList();
    paragraph.push(line.trim());
  }
  endParagraph();
  endList();
  return blocks;
}
